import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AddBeneficiarioCripto } from 'src/app/dashboard/interfaces/add-beneficiario-cripto.interface';
import { Banco } from 'src/app/dashboard/interfaces/banco';
import { TipoCuenta } from 'src/app/dashboard/interfaces/tipo-cuenta.interface';
import { RecipientCriptoService } from 'src/app/dashboard/services/recipient-cripto.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { CryptoScreeningService } from 'src/app/dashboard/services/crypto-screening.service';
import { MediosCriptoService } from 'src/app/dashboard/services/medios-cripto.service';
import {
  conRedPausada,
  esErrorRedInactiva,
  esRedBinance,
  EstadoRed,
  MEDIO_CRIPTO_TRC20,
  MENSAJE_TRON_EN_SOLANA,
  mensajeRedInactiva,
  redActiva,
  RedCripto,
  redDeBeneficiario,
  redPorId,
  REDES_CRIPTO,
  unirNombres,
  validarDireccion,
} from 'src/app/shared/utils/red-cripto';

/** Una red tal como la ofrece el selector. */
export interface OpcionRedCripto {
  red: RedCripto;
  activa: boolean;
}

@Component({
  selector: 'app-add-beneficiary',
  templateUrl: './add-beneficiary.component.html',
  styleUrls: ['./add-beneficiary.component.sass'],
})
export class AddBeneficiaryComponent implements OnInit {
  formCuenta!: FormGroup;
  esUpdate = false;

  /**
   * Motivo por el que el último intento de guardar no prosperó.
   *
   * Va en una franja dentro del diálogo y no en un `Swal`, a propósito: el
   * modal se cierra y se lleva la explicación con él, justo cuando el cliente
   * necesita tenerla a la vista para corregir. Acá queda hasta el próximo
   * intento.
   *
   * Existe porque este flujo tenía salidas que devolvían sin decir nada
   * —el formulario inválido, el `catch` vacío del guardado y, mientras se
   * pidió, el Authenticator cancelado—, y el síntoma que llegaba a soporte era
   * siempre el mismo: "hago clic y no pasa nada".
   */
  mensajeError: string | null = null;

  /**
   * VES→USDT abre este diálogo con `soloRedBinance`: ese retiro sale siempre
   * por Binance, que solo paga por TRC20. Un beneficiario de otra red
   * registrado desde ahí no aparecería en la lista al volver. Enviar USDT ya no
   * lo pasa (30/10/2026): acepta todas las redes.
   */
  readonly soloRedBinance: boolean;

  /** Encendida/pausada de cada red, de `medios-cripto`. Vacío = todas activas. */
  estadoRedes: EstadoRed[] = [];

  /** Las redes del selector: todas, o solo TRC20 con `soloRedBinance`. */
  opcionesRed: OpcionRedCripto[] = [];

  /**
   * La red elegida (o, al editar, la del beneficiario). Vive aparte del
   * control porque los validadores la leen apenas se construye el formulario,
   * antes de que `formCuenta` exista.
   */
  redElegida: number | null = null;

  constructor(
    public dialogRef: MatDialogRef<AddBeneficiaryComponent>,
    private fb: FormBuilder,
    private spinner: SpinnerService,
    private recipientcriptoservice: RecipientCriptoService,
    private screeningService: CryptoScreeningService,
    private mediosCripto: MediosCriptoService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.esUpdate = this.data?.type === 'UPDATE';
    this.soloRedBinance = this.data?.soloRedBinance === true;
    this.armarOpciones();
  }

  tiposID = ['CI', 'PASAPORTE', 'RUT'];
  bancos: Banco[] = [];
  tiposCuentas: TipoCuenta[] = [];

  async ngOnInit(): Promise<void> {
    const ref = this.spinner.start();
    try {
      this.initForm();
      await this.cargarRedes();
    } finally {
      this.spinner.stop(ref);
    }
  }

  initForm(): void {
    // Al editar, la red es la del beneficiario y no se cambia: una dirección
    // de una red no sirve en otra, y wallet-back igual la ignora en la
    // edición. Por eso el control nace deshabilitado (no se valida ni se
    // ofrece) y la red se muestra fija.
    this.redElegida = this.esUpdate
      ? redDeBeneficiario(this.data.beneficiario)
      : this.primeraRedDisponible();

    this.formCuenta = this.fb.group({
      nombre: new FormControl(
        this.esUpdate ? this.data.beneficiario.beneficiario_nombre : '',
        [
          Validators.required,
          Validators.maxLength(40),
          this.noTildesOrSpecialCharsValidator,
        ]
      ),
      apellido: new FormControl(
        this.esUpdate ? this.data.beneficiario.beneficiario_apellido : '',
        [
          Validators.required,
          Validators.maxLength(40),
          this.noTildesOrSpecialCharsValidator,
        ]
      ),
      red: new FormControl(
        { value: this.redElegida, disabled: this.esUpdate },
        [Validators.required, this.validarRed]
      ),
      // El largo ya no es fijo (34 era TRC20): cada red tiene su formato y
      // lo valida `validarFormatoDireccion` con la red elegida. Mismo formato
      // que los backends, así el alta y la edición aceptan lo mismo.
      medioRecepcion: new FormControl(
        this.esUpdate ? this.data.beneficiario.beneficiario_medio_recepcion : '',
        [Validators.required, this.validarFormatoDireccion]
      ),
    });

    // Cambiar de red cambia qué dirección es válida: lo escrito se vuelve a
    // mirar en el momento, no recién al guardar.
    this.formCuenta.get('red')?.valueChanges.subscribe((valor) => {
      this.redElegida = valor === null || valor === undefined || valor === '' ? null : Number(valor);
      this.formCuenta.get('medioRecepcion')?.updateValueAndValidity();
    });
  }

  /** La red elegida, con su formato, ayuda y placeholder. */
  get redSeleccionada(): RedCripto | null {
    return this.redElegida === null ? null : redPorId(this.redElegida);
  }

  /** Editando un beneficiario de una red pausada: se puede, pero conviene avisar. */
  get redFijaPausada(): boolean {
    return this.esUpdate && !redActiva(this.redElegida, this.estadoRedes);
  }

  /** Ninguna red del selector está activa: no hay dónde registrar. */
  get sinRedesDisponibles(): boolean {
    return !this.esUpdate && this.opcionesRed.length > 0 && this.opcionesRed.every((o) => !o.activa);
  }

  get avisoSinRedes(): string {
    if (this.soloRedBinance) {
      return (
        'La red Tron (TRC20) está pausada por el momento, así que por ahora no ' +
        'podés registrar beneficiarios para este envío. Volvé a intentarlo más tarde.'
      );
    }
    return (
      'Las redes cripto están pausadas por el momento, así que por ahora no ' +
      'podés registrar beneficiarios. Volvé a intentarlo más tarde.'
    );
  }

  /**
   * Lee qué redes están encendidas y lo refleja en el selector. `listar()`
   * nunca rechaza: si Nest no responde, todas cuentan como activas y el que
   * decide es wallet-back al guardar.
   */
  private async cargarRedes(): Promise<void> {
    this.estadoRedes = await this.mediosCripto.listar();
    this.aplicarEstadoRedes();
  }

  private aplicarEstadoRedes(): void {
    this.armarOpciones();
    const control = this.formCuenta?.get('red');
    if (!control) return;
    // Al crear, si el cliente todavía no tocó el selector y la red sugerida
    // resultó pausada, se pasa a la primera activa en vez de dejarlo en una
    // opción que no puede usar.
    if (!this.esUpdate && !control.dirty && !redActiva(control.value, this.estadoRedes)) {
      control.setValue(this.primeraRedDisponible());
    }
    control.updateValueAndValidity();
  }

  private armarOpciones(): void {
    const permitidas = this.soloRedBinance
      ? REDES_CRIPTO.filter((red) => red.id === MEDIO_CRIPTO_TRC20)
      : REDES_CRIPTO;
    this.opcionesRed = permitidas.map((red) => ({
      red,
      activa: redActiva(red.id, this.estadoRedes),
    }));
  }

  /** TRC20 si está activa (es la de siempre); si no, la primera activa; si no hay, TRC20 igual. */
  private primeraRedDisponible(): number {
    return this.opcionesRed.find((o) => o.activa)?.red.id ?? MEDIO_CRIPTO_TRC20;
  }

  /**
   * La red tiene que estar en el catálogo, entre las que ofrece el diálogo y
   * encendida. Como validador y no como `disabled` del botón: así
   * `guardarBeneficiario()` puede decir qué pasa, igual que con los demás
   * campos.
   */
  private validarRed = (control: AbstractControl): ValidationErrors | null => {
    const valor = control.value;
    if (valor === null || valor === undefined || valor === '') return null;
    if (!redPorId(valor) || (this.soloRedBinance && !esRedBinance(valor))) {
      return { redNoPermitida: true };
    }
    if (!redActiva(valor, this.estadoRedes)) {
      return { redPausada: true };
    }
    return null;
  };

  /**
   * El error lleva el motivo (el mismo texto que respondería wallet-back), y
   * el `mat-error` lo muestra tal cual: una dirección Tron pegada con Solana
   * elegida no tiene el "formato" mal, es de otra red, y hay que decirlo así.
   */
  private validarFormatoDireccion = (control: AbstractControl): ValidationErrors | null => {
    const valor = typeof control.value === 'string' ? control.value.trim() : '';
    if (!valor || this.redElegida === null) return null;
    const motivo = validarDireccion(this.redElegida, valor);
    return motivo ? { direccionInvalida: motivo } : null;
  };

  noTildesOrSpecialCharsValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value;
    const validRegex = /^[a-zA-Z ]*$/;

    if (value && !validRegex.test(value)) {
      return { tildesOrSpecialChars: true };
    }
    return null;
  }

  soloAlfabetico(event: KeyboardEvent) {
    const teclasPermitidas = [
      'Backspace',
      'Tab',
      'End',
      'Home',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
    ];
    if (teclasPermitidas.indexOf(event.key) !== -1) {
      return;
    }
    const patron = /^[a-zA-Z\s]+$/;
    if (!patron.test(event.key)) {
      event.preventDefault();
    }
  }

  soloNumeros(event: KeyboardEvent) {
    const char = event.key;
    if (!char.match(/[0-9]/)) {
      event.preventDefault();
    }
  }

  async guardarBeneficiario() {
    this.mensajeError = null;

    if (this.formCuenta.invalid) {
      // El botón ya no se deshabilita solo, así que acá sí llegan formularios
      // incompletos. `markAllAsTouched` es lo que destapa los `mat-error` que
      // el template ya tenía escritos: sin esto los campos arrancan
      // `untouched` y Angular Material no pinta ninguno.
      this.formCuenta.markAllAsTouched();
      this.mensajeError = this.describirFormInvalido();
      return;
    }

    const red = this.redSeleccionada;
    if (!red) {
      this.mensajeError = 'Elegí la red de la wallet.';
      return;
    }

    // 30/10/2026: registrar o editar un beneficiario ya no pide el código del
    // Authenticator. Muchos clientes no lograban completarlo y quedaban sin
    // poder agregar wallets. El inicio de sesión no cambia: Cognito se lo sigue
    // pidiendo a quien lo tenga activo. Antes de guardar queda el screening de
    // la dirección.

    // Solo las columnas de `beneficiario_criptomoneda`. Antes se mandaba el
    // formulario entero, con un `recepcion: 'TRC20'` que no es columna y un
    // `medioCriptoId: 1` fijo: la red ahora es la elegida.
    const formValue = this.formCuenta.getRawValue();
    const direccion = String(formValue?.medioRecepcion ?? '').trim();
    const cuentaBeneficiary: AddBeneficiarioCripto = {
      nombre: formValue?.nombre,
      apellido: formValue?.apellido,
      medioRecepcion: direccion,
      medioCriptoId: red.id,
      usuarioId: this.data.userID,
    };
    if (this.esUpdate) {
      cuentaBeneficiary['id'] = this.data.beneficiario.beneficiario_id;
    }

    // Prevalidación de la dirección antes de darla de alta. Si el backend
    // responde que está bloqueada, no se llama a la lambda de registro; si no
    // responde, el alta sigue como siempre (ver CryptoScreeningService).
    const habilitada = await this.verificarWallet(direccion, red);
    if (!habilitada) {
      return;
    }

    const ref = this.spinner.start();
    try {
      await this.recipientcriptoservice.saveRecipientAccount(cuentaBeneficiary);
      Swal.fire({
        icon: 'success',
        title: this.esUpdate ? 'Beneficiario actualizado' : 'Beneficiario creado',
        text: this.esUpdate
          ? 'Su Beneficiario fue actualizado con éxito'
          : 'Su Beneficiario fue registrado con éxito',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',
      });
      this.dialogRef.close(true);
    } catch (error) {
      if (esErrorRedInactiva(error)) {
        // La red se pausó con el diálogo abierto. Se refleja ya en el
        // selector —el backend acaba de decirlo— y la próxima lectura va a
        // Nest en vez de a la caché.
        this.mediosCripto.invalidar();
        this.estadoRedes = conRedPausada(this.estadoRedes, red.id);
        this.armarOpciones();
        this.formCuenta.get('red')?.updateValueAndValidity();
      }
      // Este `catch` estaba vacío. Se comía todo: el 400 de "ya tenés esta
      // dirección" que la lambda devuelve desde 2024, el 423 del bloqueo por
      // actualización de datos, y hasta la red caída. El cliente veía el
      // diálogo intacto, sin éxito ni error.
      this.mensajeError = this.describirErrorGuardado(error);
    }
    this.spinner.stop(ref);
  }

  /**
   * Traduce el estado del formulario a una frase accionable.
   *
   * Devuelve el primer problema encontrado y no la lista entera: el cliente
   * corrige de a uno, y una enumeración de cuatro reglas asusta más de lo que
   * ayuda.
   */
  private describirFormInvalido(): string {
    // La red primero: de ella depende qué dirección es válida.
    const red = this.formCuenta.get('red');
    if (red?.hasError('required')) {
      return 'Elegí la red de la wallet.';
    }
    if (red?.hasError('redNoPermitida')) {
      return this.soloRedBinance
        ? 'Para este envío la wallet tiene que ser de la red Tron (TRC20).'
        : 'Elegí una de las redes de la lista.';
    }
    if (red?.hasError('redPausada')) {
      const pausada = this.redSeleccionada?.etiqueta ?? 'elegida';
      const otras = this.opcionesRed.filter((o) => o.activa).map((o) => o.red.etiqueta);
      return otras.length
        ? `La red ${pausada} está pausada por el momento. Por ahora podés registrar ` +
            `beneficiarios en ${unirNombres(otras)}.`
        : `La red ${pausada} está pausada por el momento, así que por ahora no podés ` +
            'registrar beneficiarios en ella. Volvé a intentarlo más tarde.';
    }

    const direccion = this.formCuenta.get('medioRecepcion');
    if (direccion?.hasError('required')) {
      return 'Ingresá la dirección de la wallet.';
    }
    if (direccion?.hasError('direccionInvalida')) {
      return this.describirDireccionInvalida();
    }

    const nombre = this.formCuenta.get('nombre');
    if (nombre?.hasError('required')) {
      return 'Completá el nombre del beneficiario.';
    }
    if (nombre?.hasError('tildesOrSpecialChars')) {
      return 'El nombre no puede llevar tildes, ñ ni caracteres especiales.';
    }
    if (nombre?.hasError('maxlength')) {
      return 'El nombre no puede superar los 40 caracteres.';
    }

    const apellido = this.formCuenta.get('apellido');
    if (apellido?.hasError('required')) {
      return 'Completá el apellido del beneficiario.';
    }
    if (apellido?.hasError('tildesOrSpecialChars')) {
      return 'El apellido no puede llevar tildes, ñ ni caracteres especiales.';
    }
    if (apellido?.hasError('maxlength')) {
      return 'El apellido no puede superar los 40 caracteres.';
    }

    return 'Revisá los campos marcados en rojo para poder continuar.';
  }

  /**
   * El caso que más se ve y el que peor se explicaba: una dirección de otra
   * red entra sin problema en el campo y el formato no la acepta. Además del
   * formato de la red elegida, si la dirección es de otra red que se puede
   * elegir acá, se nombra: ahorra el ticket.
   */
  private describirDireccionInvalida(): string {
    const red = this.redSeleccionada;
    const valor = String(this.formCuenta.get('medioRecepcion')?.value ?? '').trim();
    const motivo = validarDireccion(this.redElegida, valor) ?? 'Revisá la dirección de la wallet.';
    if (motivo === MENSAJE_TRON_EN_SOLANA) {
      // Ya dice de qué red es y qué hacer.
      return motivo;
    }
    const otra = this.opcionesRed.find(
      (o) => o.activa && o.red.id !== red?.id && validarDireccion(o.red.id, valor) === null
    );
    if (otra) {
      return `${motivo} Parece una dirección de ${otra.red.etiqueta}: si es así, elegí esa red.`;
    }
    if (this.soloRedBinance) {
      return (
        `${motivo} Si copiaste una dirección de otra red —ERC20, BEP20 o Polygon— ` +
        'no sirve para este envío.'
      );
    }
    return motivo;
  }

  /**
   * Traduce lo que rebotó del backend.
   *
   * Ojo con la forma de `error`, que no es una sola: el `ErrorInterceptor`
   * relanza los 400/422/429 como `HttpErrorResponse` intacto —ahí vive el
   * mensaje de la lambda— pero aplana todo lo demás a un string. Y el
   * `DataUpdateBlockInterceptor` corta antes con un objeto sintético
   * `{ status: 423 }` que nunca pasa por el `ErrorInterceptor`. Por eso se
   * mira `status` con cuidado y se acepta que llegue un string pelado.
   */
  private describirErrorGuardado(error: any): string {
    const generico =
      'No pudimos guardar el beneficiario. Intentá de nuevo en un momento y, ' +
      'si sigue igual, escribinos a soporte.';

    if (typeof error === 'string') {
      return error || generico;
    }

    // 422 `RED_CRIPTO_INACTIVA`: la red se pausó. El texto del backend ya
    // nombra la red y dice cuáles se pueden usar.
    if (esErrorRedInactiva(error)) {
      return mensajeRedInactiva(error);
    }

    const status = error?.status;

    if (status === 423 || error?.error?.code === 'USER_BLOCKED_BY_PENDING_UPDATE') {
      return (
        'Tenés una actualización de datos pendiente. Completala desde el inicio ' +
        'para volver a registrar beneficiarios.'
      );
    }

    if (status === 401) {
      return 'Tu sesión venció. Volvé a iniciar sesión e intentá de nuevo.';
    }

    if (status === 0) {
      return 'No pudimos conectarnos. Revisá tu conexión e intentá de nuevo.';
    }

    // El mensaje de la lambda, cuando lo hay. Es el que dice "Ya hay un
    // beneficiario con esa dirección asociado a este usuario", y el de una
    // dirección que no corresponde a la red.
    const delBackend = error?.error?.message;
    if (typeof delBackend === 'string' && delBackend.trim()) {
      return delBackend;
    }

    return generico;
  }

  /**
   * Consulta el screening de la dirección. Devuelve `false` solo cuando el
   * backend la bloquea explícitamente: cualquier otra cosa deja pasar.
   */
  private async verificarWallet(medioRecepcion: string, red: RedCripto): Promise<boolean> {
    const ref = this.spinner.start();
    try {
      // Con el código de la red elegida (TRC20, POLYGON, SOL): es el
      // `network` que entiende el screening.
      const resultado = await this.screeningService.verificar(
        (medioRecepcion || '').trim(),
        red.codigo,
        'REGISTRO',
      );

      if (resultado.permitido) {
        return true;
      }

      const motivo =
        resultado.mensaje ||
        'Nuestro sistema de prevención identificó esta dirección como riesgosa. Escríbenos a soporte si crees que es un error.';

      await Swal.fire({
        icon: 'error',
        title: 'No podemos registrar esta dirección',
        text: motivo,
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',
      });
      // El `Swal` se cierra y se lleva el motivo. Queda también en la franja
      // para que siga a la vista mientras el cliente decide qué hacer.
      this.mensajeError = motivo;
      return false;
    } finally {
      this.spinner.stop(ref);
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
